import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/shared/ui/dialog';
import { Building2, Eye, Plus, ChevronRight, Users, Layers, TrendingUp, Zap, ClipboardCheck } from 'lucide-react';
import {
  creerSousCompte, getOverview, getSousComptes, observerSousCompte,
  LIBELLES_ACTION, NIVEAUX,
} from '@/shared/api/superadmin';
import { toast } from '@/shared/ui/sonner';

const dateCourte = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
};

function Tuile({ libelle, valeur, detail, testid, icone: Icone, teinte }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{libelle}</p>
          {Icone && <Icone className={`h-4 w-4 shrink-0 ${teinte || 'text-slate-400'}`} aria-hidden="true" />}
        </div>
        <p className="mt-1 text-2xl font-bold tabular-nums text-slate-900" data-testid={testid}>{valeur}</p>
        {detail && <p className="mt-0.5 text-[11px] text-slate-500">{detail}</p>}
      </CardContent>
    </Card>
  );
}

export default function ConsolePage() {
  const navigate = useNavigate();

  /**
   * Entre en observation sur un sous-compte, sans perdre la session en cours.
   *
   * Le jeton du super administrateur est mis de cote avant d'etre remplace : le
   * bandeau d'observation le remettra en place a la sortie. Sans cela, quitter
   * l'observation obligerait a se reconnecter.
   */
  const observer = async (id, nom) => {
    try {
      const { token } = await observerSousCompte(id);
      const jetonCourant = localStorage.getItem('flexcore_token');
      if (jetonCourant) localStorage.setItem('flexcore_jeton_superadmin', jetonCourant);
      localStorage.setItem('flexcore_token', token);
      window.location.href = '/dashboard';
    } catch (e) {
      toast.error(e.response?.data?.detail || `Observation impossible sur « ${nom} »`);
    }
  };
  const [vue, setVue] = useState(null);
  const [comptes, setComptes] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [dialogue, setDialogue] = useState(false);
  const [nom, setNom] = useState('');
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async () => {
    try {
      const [o, t] = await Promise.all([getOverview(), getSousComptes()]);
      setVue(o);
      setComptes(t);
    } catch (e) {
      console.error(e);
    }
    setChargement(false);
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const creer = async () => {
    setErreur(''); setEnvoi(true);
    try {
      const cree = await creerSousCompte(nom.trim());
      setDialogue(false); setNom('');
      await charger();
      navigate(`/superadmin/sous-comptes/${cree.id}`);
    } catch (e) {
      setErreur(e?.response?.data?.detail || "La création a échoué");
    }
    setEnvoi(false);
  };

  if (chargement) {
    return <div className="p-6 text-sm text-slate-500">Chargement…</div>;
  }

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="console-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
          Sous-comptes
        </h1>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Tuile
          libelle="Sous-comptes" testid="kpi-sous-comptes" icone={Building2} teinte="text-rose-500"
          valeur={vue?.sous_comptes?.total ?? 0}
          detail={`${vue?.sous_comptes?.actifs ?? 0} actifs · ${vue?.sous_comptes?.suspendus ?? 0} suspendus`}
        />
        <Tuile
          libelle="Comptes" testid="kpi-comptes" icone={Users} teinte="text-cyan-600"
          valeur={vue?.comptes?.total ?? 0}
          detail={`${vue?.comptes?.par_role?.admin ?? 0} admin · ${vue?.comptes?.par_role?.commercial ?? 0} gestionnaire`}
        />
        <Tuile
          libelle="Clients" testid="kpi-clients" icone={TrendingUp} teinte="text-amber-600"
          valeur={vue?.volumes?.clients ?? 0} detail="toutes plateformes"
        />
        <Tuile
          libelle="Actifs" testid="kpi-actifs" icone={Zap} teinte="text-violet-600"
          valeur={vue?.volumes?.actifs ?? 0} detail="toutes plateformes"
        />
        {/* Le nombre en vigueur d'abord, le total en second : c'est le mandat actif qui
            autorise à piloter et à facturer. Le serveur renvoyait les deux, l'écran
            n'en montrait aucun. */}
        <Tuile
          libelle="Mandats actifs" testid="kpi-mandats" icone={ClipboardCheck} teinte="text-blue-600"
          valeur={vue?.volumes?.mandats_actifs ?? 0}
          detail={`${vue?.volumes?.mandats ?? 0} au total`}
        />
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-900">
          Les sous-comptes
        </h2>
        <Button onClick={() => setDialogue(true)} data-testid="creer-sous-compte-btn" size="sm">
          <Plus className="mr-1 h-4 w-4" /> Nouveau sous-compte
        </Button>
      </div>

      <div className="space-y-2">
        {comptes.map((c) => (
          <button
            key={c.id}
            onClick={() => navigate(`/superadmin/sous-comptes/${c.id}`)}
            data-testid={`sous-compte-${c.id}`}
            className="flex w-full items-center gap-4 rounded-lg border border-slate-200 bg-white p-4 text-left transition hover:border-slate-300 hover:bg-slate-50"
          >
            <Building2 className="h-5 w-5 shrink-0 text-slate-400" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-slate-900">{c.name}</p>
              <p className="mt-0.5 flex flex-wrap items-center gap-x-3 text-[12px] text-slate-500">
                <span className="inline-flex items-center gap-1">
                  <Users className="h-3 w-3" />{c.users_count} compte{c.users_count > 1 ? 's' : ''}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Layers className="h-3 w-3" />{c.clients_count} client{c.clients_count > 1 ? 's' : ''}
                </span>
              </p>
            </div>
            <Badge
              variant={c.status === 'actif' ? 'default' : 'destructive'}
              data-testid={`statut-${c.id}`}
              className={c.status === 'actif' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : ''}
            >
              {c.status === 'actif' ? 'Actif' : 'Suspendu'}
            </Badge>
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); observer(c.id, c.name); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); observer(c.id, c.name); }
              }}
              title="Observer ce sous-compte en lecture seule"
              data-testid={`observer-${c.id}`}
              className="shrink-0 rounded p-1.5 text-slate-400 transition hover:bg-amber-50 hover:text-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-400"
            >
              <Eye className="h-4 w-4" />
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
          </button>
        ))}
      </div>

      {/* Les cinq dernières actions de plateforme. Le serveur les renvoyait déjà dans la
          vue d'ensemble et aucun écran ne les lisait : la console ne montrait donc rien
          de ce qu'elle venait de faire, sans ouvrir le journal complet. */}
      {vue?.journal_recent?.length > 0 && (
        <section data-testid="activite-recente">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-900">
              Activité récente
            </h2>
            <Button
              variant="ghost" size="sm" className="text-slate-500"
              onClick={() => navigate('/superadmin/journal')}
              data-testid="voir-journal-btn"
            >
              Voir le journal <ChevronRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          </div>
          <ul className="space-y-1">
            {vue.journal_recent.map((l) => {
              const niveau = NIVEAUX[l.niveau] || NIVEAUX.info;
              return (
                <li
                  key={l.id}
                  className="flex flex-wrap items-baseline gap-x-3 rounded border border-slate-200 bg-white px-3 py-2 text-[12px]"
                  data-testid={`activite-${l.id}`}
                >
                  <Badge className={`${niveau.teinte} hover:bg-inherit`}>{niveau.libelle}</Badge>
                  <span className="font-medium text-slate-900">
                    {LIBELLES_ACTION[l.action] || l.action}
                  </span>
                  <span className="text-slate-500">{l.cible_nom || '—'}</span>
                  <span className="ml-auto text-slate-400">{dateCourte(l.created_at)}</span>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <Dialog open={dialogue} onOpenChange={setDialogue}>
        <DialogContent data-testid="dialog-creer-sous-compte">
          <DialogHeader>
            <DialogTitle>Nouveau sous-compte</DialogTitle>
            <DialogDescription>
              Un espace vide et cloisonné. Il faudra ensuite lui poser un premier
              administrateur pour que quelqu&apos;un puisse s&apos;y connecter.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="nom-sous-compte">Nom</Label>
            <Input
              id="nom-sous-compte" value={nom} onChange={(e) => setNom(e.target.value)}
              placeholder="Nom de l'entreprise" data-testid="input-nom-sous-compte"
            />
            {erreur && <p className="text-sm text-red-600" data-testid="erreur-creation">{erreur}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogue(false)}>Annuler</Button>
            <Button
              onClick={creer} disabled={nom.trim().length < 2 || envoi}
              data-testid="valider-creation-btn"
            >
              {envoi ? 'Création…' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
